import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { StyleSheet, View, Platform, ScrollView, Pressable } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import Button from '@/components/Buttons/Button';
import SettingsSection from '@/components/SettingsSection';
import LocalConversationImport from './LocalConversationImport';
import Icon from '@/components/Icon';
import * as DocumentPicker from 'expo-document-picker';
import { useToastStore, useChatStore, useCollectionStore } from '@/store';
import { useTranslation } from '@/hooks/useTranslation';
import { chatsApi } from '@/lib/api';
import {
  extractConversationsFromZip,
  parseConversationJson,
  parseConversations,
  detectSource,
  batchConversationsAsync,
} from '@/constants/utils/conversationImport';
import { importMetadataKind, collectImportMetadata, parseImportCollections } from '@/constants/utils/importCollections';

const IMPORT_STATES = {
  IDLE: 'idle',
  READING_FILE: 'reading_file',
  PARSING: 'parsing',
  /** Splitting conversations into HTTP batches (can take a while on large exports). */
  PREPARING_UPLOAD: 'preparing_upload',
  UPLOADING: 'uploading',
  DONE: 'done',
  ERROR: 'error',
};

function ImportSettings() {
  const { colors } = useTheme();
  const { addToast } = useToastStore((state) => state.actions);
  const fetchChats = useChatStore((state) => state.fetchChats);
  const fetchCollections = useCollectionStore((state) => state.fetchCollections);
  const { t } = useTranslation();

  const [collapsedProviders, setCollapsedProviders] = useState({});
  const [localBusy, setLocalBusy] = useState(false);
  const [activeProvider, setActiveProvider] = useState(null);
  const [importState, setImportState] = useState(IMPORT_STATES.IDLE);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  /** Progress while splitting conversations into upload batches (source conversations, not HTTP batches). */
  const [prepareProgress, setPrepareProgress] = useState({ current: 0, total: 0 });
  /** When importing multiple files: which file is being read/parsed (1-based display). */
  const [fileProgress, setFileProgress] = useState({ index: 0, total: 0 });
  const [result, setResult] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [detectedSource, setDetectedSource] = useState(null);
  const cancelledRef = useRef(false);
  const pickingRef = useRef(false);
  useEffect(() => () => { cancelledRef.current = true; }, []);

  const styles = useMemo(() => createStyles(colors), [colors]);

  const resetState = useCallback(() => {
    cancelledRef.current = false;
    setImportState(IMPORT_STATES.IDLE);
    setProgress({ current: 0, total: 0 });
    setPrepareProgress({ current: 0, total: 0 });
    setFileProgress({ index: 0, total: 0 });
    setResult(null);
    setErrorMessage(null);
    setDetectedSource(null);
  }, []);

  const handleCancel = useCallback(() => {
    cancelledRef.current = true;
  }, []);

  const releasePickerAsset = (asset) => {
    try {
      if (
        (Platform.OS === 'web' || Platform.OS === 'macos') &&
        asset?.uri &&
        typeof asset.uri === 'string' &&
        asset.uri.startsWith('blob:')
      ) {
        URL.revokeObjectURL(asset.uri);
      }
    } catch {
      /* noop */
    }
  };

  const isZipAsset = (asset) =>
    asset.mimeType?.includes('zip') || asset.name?.toLowerCase().endsWith('.zip');
  const isJsonAsset = (asset) =>
    asset.mimeType?.includes('json') || asset.name?.toLowerCase().endsWith('.json');

  /**
   * ChatGPT exports are often multi‑MB JSON. On web, DocumentPicker defaults to
   * base64-inlining the whole file (heavy) and fetch(blob:) can then fail with
   * "Failed to fetch". Prefer the underlying File when expo provides it.
   */
  const readPickerAssetAsArrayBuffer = async (asset) => {
    if (asset.file && typeof asset.file.arrayBuffer === 'function') {
      return asset.file.arrayBuffer();
    }
    if (Platform.OS === 'web' || Platform.OS === 'macos') {
      const response = await fetch(asset.uri);
      if (!response.ok) {
        throw new Error(`Could not read file (HTTP ${response.status})`);
      }
      return response.arrayBuffer();
    }

    const FileSystem = require('expo-file-system');
    const base64 = await FileSystem.readAsStringAsync(asset.uri, {
      encoding: FileSystem.EncodingType.Base64,
    });
    return Uint8Array.from(atob(base64), (c) => c.charCodeAt(0)).buffer;
  };

  const readPickerAssetAsText = async (asset) => {
    if (asset.file && typeof asset.file.text === 'function') {
      return asset.file.text();
    }
    if (Platform.OS === 'web' || Platform.OS === 'macos') {
      const response = await fetch(asset.uri);
      if (!response.ok) {
        throw new Error(`Could not read file (HTTP ${response.status})`);
      }
      return response.text();
    }
    const FileSystem = require('expo-file-system');
    return FileSystem.readAsStringAsync(asset.uri);
  };

  const humanizeImportError = (err) => {
    const msg = err?.message || '';
    if (/failed to fetch/i.test(msg)) {
      return t('settings.import.errorFailedToFetch');
    }
    return msg || t('settings.import.unexpectedError');
  };

  const loadOneAsset = async (file, fileLabel) => {
    const isZip = isZipAsset(file);
    const isJson = isJsonAsset(file);

    if (!isZip && !isJson) {
      releasePickerAsset(file);
      throw new Error(
        t('settings.import.invalidFileInSelection').replace('%NAME%', fileLabel)
      );
    }

    setImportState(IMPORT_STATES.READING_FILE);
    let rawConversations;
    try {
      if (isZip) {
        const arrayBuffer = await readPickerAssetAsArrayBuffer(file);
        rawConversations = await extractConversationsFromZip(arrayBuffer, { includeCollections: true });
      } else {
        const jsonString = await readPickerAssetAsText(file);
        try {
          rawConversations = parseConversationJson(jsonString);
        } catch {
          throw new Error(
            t('settings.import.invalidJsonInFile').replace('%NAME%', fileLabel)
          );
        }
      }
    } finally {
      releasePickerAsset(file);
    }

    return { rawConversations, metadataKind: isJson ? importMetadataKind(fileLabel) : null };
  };

  const handleImport = async (provider) => {
    if (pickingRef.current || localBusy) return;
    pickingRef.current = true;
    setActiveProvider(provider);
    try {
      resetState();
      cancelledRef.current = false;

      setImportState(IMPORT_STATES.READING_FILE);
      const pickerResult = await DocumentPicker.getDocumentAsync({
        // MIME types cover iOS/Android UTIs; the bare extensions matter on
        // web where some browsers/OSes don't have `application/json`
        // registered for `.json` files (ChatGPT exports get greyed out in
        // the dialog when only MIME types are listed). Native picker
        // implementations silently ignore extension-only entries.
        type: [
          'application/zip',
          'application/x-zip-compressed',
          'application/json',
          '.zip',
          '.json',
        ],
        copyToCacheDirectory: true,
        base64: false,
        multiple: true,
      });

      if (pickerResult.canceled || !pickerResult.assets?.length) { resetState(); return; }

      const assets = pickerResult.assets;
      const n = assets.length;
      setFileProgress({ index: 1, total: n });

      let mergedSource = null;
      const allConversations = [];
      const allErrors = [];
      const loadedFiles = [];
      const collectionsById = new Map();
      const collectionWarnings = new Set();

      for (let i = 0; i < assets.length; i++) {
        const file = assets[i];
        const label = file.name || `file ${i + 1}`;
        setFileProgress({ index: i + 1, total: n });

        try {
          loadedFiles.push({ label, ...(await loadOneAsset(file, label)) });
        } catch (err) {
          allErrors.push(`${label}: ${humanizeImportError(err)}`);
        }
      }

      // Read companion files before parsing any chats, so selection order
      // cannot turn a named project into a placeholder or lose membership.
      const collectionMetadata = loadedFiles.filter(file => file.metadataKind || detectSource(file.rawConversations) === provider)
        .flatMap(file => file.metadataKind
        ? [{ kind: file.metadataKind, rows: file.rawConversations }]
        : collectImportMetadata(file.rawConversations));
      setImportState(IMPORT_STATES.PARSING);
      for (const file of loadedFiles.filter(file => !file.metadataKind)) {
        try {
          const { source, conversations, parseErrors, collections, collectionWarnings: warnings } =
            await parseConversations(file.rawConversations, { collectionMetadata, sourceHint: provider });
          if (source !== provider) {
            throw new Error(t('settings.import.sourceMismatch'));
          }
          if (mergedSource === null) {
            mergedSource = source;
          } else if (source !== mergedSource) {
            setImportState(IMPORT_STATES.ERROR);
            setErrorMessage(t('settings.import.mixedSourcesError'));
            addToast(t('settings.import.mixedSourcesError'), 'error');
            return;
          }
          allErrors.push(...(parseErrors || []).map(e => `${file.label}: ${e.title} — ${e.error}`));
          allConversations.push(...conversations);
          for (const collection of collections) {
            const previous = collectionsById.get(collection.externalId);
            if (!previous || previous.nameMissing) collectionsById.set(collection.externalId, collection);
            else if (previous.title !== collection.title || previous.parentExternalId !== collection.parentExternalId) {
              collectionWarnings.add(`Conflicting folder metadata for "${collection.title}"; the first definition was kept.`);
            }
          }
          for (const warning of warnings) collectionWarnings.add(warning);
        } catch (err) {
          allErrors.push(`${file.label}: ${humanizeImportError(err)}`);
        } finally {
          file.rawConversations = null;
        }
      }
      if (!loadedFiles.some(file => !file.metadataKind) && collectionMetadata.length) {
        const parsed = parseImportCollections(provider, [], collectionMetadata);
        for (const collection of parsed.collections) collectionsById.set(collection.externalId, collection);
        for (const warning of parsed.collectionWarnings) collectionWarnings.add(warning);
        mergedSource = provider;
      }

      setDetectedSource(mergedSource);

      if (allConversations.length === 0 && collectionsById.size === 0) {
        setImportState(IMPORT_STATES.ERROR);
        setErrorMessage(
          allErrors.length
            ? `${t('settings.import.noConversationsFound')}\n\n${allErrors.slice(0, 5).join('\n')}`
            : t('settings.import.noConversationsFound')
        );
        addToast(t('settings.import.importFailed'), 'error');
        return;
      }

      const source = mergedSource;
      const conversations = allConversations;
      const collections = [...collectionsById.values()];

      setImportState(IMPORT_STATES.PREPARING_UPLOAD);
      setFileProgress({ index: 0, total: 0 });
      setPrepareProgress({ current: 0, total: conversations.length });

      const batches = await batchConversationsAsync(
        source,
        conversations,
        (processed, total) => {
          setPrepareProgress({ current: processed, total });
        }
      );
      if (batches.length === 0 && collections.length > 0) batches.push([]);

      const totalPreparedConversations = batches.reduce(
        (sum, batch) => sum + batch.length,
        0
      );

      setImportState(IMPORT_STATES.UPLOADING);
      setProgress({ current: 0, total: totalPreparedConversations });
      setPrepareProgress({ current: 0, total: 0 });

      let totalImported = 0;
      let totalSkipped = 0;
      let processedConversations = 0;
      let totalOrganized = 0;
      const restoredCollectionIds = new Set();

      for (let b = 0; b < batches.length; b++) {
        if (cancelledRef.current) break;

        const batch = batches[b];

        try {
          const response = collections.length
            ? await chatsApi.importConversations(source, batch, { collections, shouldContinue: () => !cancelledRef.current })
            : await chatsApi.importConversations(source, batch);
          totalImported += response.imported || 0;
          totalSkipped += response.skipped || 0;
          totalOrganized += response.organized || 0;
          for (const collection of response.collections || []) restoredCollectionIds.add(collection.id);

          if (response.errors?.length) {
            allErrors.push(
              ...response.errors.map(
                (e) => `${e.title || 'Unknown'}: ${e.error || e.message}`
              )
            );
          }
          if (response.stopped) break;
        } catch (err) {
          allErrors.push(`Batch ${b + 1}: ${err.message || 'Upload failed'}`);
          const status = err?.status;
          if (status === 401 || status === 403 || status === 429) {
            processedConversations += batch.length;
            setProgress({ current: processedConversations, total: totalPreparedConversations });
            break;
          }
        }

        processedConversations += batch.length;
        setProgress({
          current: processedConversations,
          total: totalPreparedConversations,
        });
      }

      setResult({
        imported: totalImported,
        skipped: totalSkipped,
        errors: allErrors,
        total: totalPreparedConversations,
        filesProcessed: n,
        collectionsRestored: restoredCollectionIds.size,
        collectionWarnings: [...collectionWarnings],
        hasCollectionMetadata: collections.length > 0,
      });
      setImportState(IMPORT_STATES.DONE);

      const sourceName =
        source === 'claude'
          ? 'Claude'
          : source === 'grok'
            ? 'Grok'
            : source === 'gabai'
              ? 'Gab AI'
              : 'ChatGPT';
      if (totalImported > 0) {
        addToast(
          t('settings.import.importSuccess')
            .replace('%COUNT%', String(totalImported))
            .replace('%SOURCE%', sourceName),
          'success'
        );
      } else if (restoredCollectionIds.size > 0) {
        addToast(t('settings.import.collectionsRestored').replace('%COUNT%', String(restoredCollectionIds.size)), 'success');
      } else if (totalSkipped > 0) {
        addToast(t('settings.import.importOnlyDuplicates'), 'warning');
      } else {
        addToast(t('settings.import.importFailedNothingImported'), 'error');
      }
      if (totalImported > 0 || totalOrganized > 0) fetchChats(1);
      if (restoredCollectionIds.size > 0) fetchCollections(1);
    } catch (err) {
      console.error('Import error:', err);
      setImportState(IMPORT_STATES.ERROR);
      const friendly = humanizeImportError(err);
      setErrorMessage(friendly);
      addToast(friendly, 'error');
    } finally { pickingRef.current = false; }
  };

  const getStatusText = () => {
    switch (importState) {
      case IMPORT_STATES.READING_FILE:
        if (fileProgress.total > 1) {
          return t('settings.import.readingFileProgress')
            .replace('%CURRENT%', String(fileProgress.index))
            .replace('%TOTAL%', String(fileProgress.total));
        }
        return t('settings.import.readingFile');
      case IMPORT_STATES.PARSING:
        if (fileProgress.total > 1) {
          return t('settings.import.parsingFileProgress')
            .replace('%CURRENT%', String(fileProgress.index))
            .replace('%TOTAL%', String(fileProgress.total));
        }
        return t('settings.import.parsingConversations');
      case IMPORT_STATES.PREPARING_UPLOAD:
        if (prepareProgress.total > 0) {
          return t('settings.import.preparingUploadProgress')
            .replace('%CURRENT%', String(prepareProgress.current))
            .replace('%TOTAL%', String(prepareProgress.total));
        }
        return t('settings.import.preparingUpload');
      case IMPORT_STATES.UPLOADING:
        return t('settings.import.importingProgress')
          .replace('%CURRENT%', String(progress.current))
          .replace('%TOTAL%', String(progress.total));
      default:
        return '';
    }
  };

  const isProcessing = [
    IMPORT_STATES.READING_FILE,
    IMPORT_STATES.PARSING,
    IMPORT_STATES.PREPARING_UPLOAD,
    IMPORT_STATES.UPLOADING,
  ].includes(importState);

  const showIndeterminateBar =
    importState === IMPORT_STATES.READING_FILE || importState === IMPORT_STATES.PARSING;
  const showPrepareBar =
    importState === IMPORT_STATES.PREPARING_UPLOAD && prepareProgress.total > 0;
  const showUploadBar =
    importState === IMPORT_STATES.UPLOADING && progress.total > 0;
  const showProgressBar = showIndeterminateBar || showPrepareBar || showUploadBar;
  const progressBarPercent = showPrepareBar
    ? Math.round((prepareProgress.current / prepareProgress.total) * 100)
    : showUploadBar
      ? Math.round((progress.current / progress.total) * 100)
      : 0;

  return (
    <View style={{ width: '100%', maxWidth: 760 }}>
      <LocalConversationImport disabled={isProcessing} onBusyChange={setLocalBusy} />
      <View style={{ gap: 12 }}>
        <Text size="s" weight="medium" color="secondary">{t('settings.import.exportHeading')}</Text>
        <View style={styles.providerGroup}>
          {[
            { id: 'chatgpt', name: 'ChatGPT', prefix: 'chatGPT' },
            { id: 'claude', name: 'Claude', prefix: 'claude' },
            { id: 'grok', name: 'Grok', prefix: 'grok' },
            { id: 'gabai', name: 'Gab AI', prefix: 'gabAI' },
          ].map((provider, index) => (
            <View key={provider.id} style={index > 0 && styles.providerDivider}>
              <View style={styles.providerRow}>
                <Pressable
                  accessibilityRole="button"
                  accessibilityLabel={t('settings.import.exportHelp', { source: provider.name })}
                  accessibilityState={{ expanded: !collapsedProviders[provider.id] }}
                  onPress={() => setCollapsedProviders(current => ({ ...current, [provider.id]: !current[provider.id] }))}
                  style={({ pressed }) => [styles.providerLabel, { opacity: pressed ? 0.6 : 1 }]}
                >
                  <View style={styles.providerIcon}><Icon name="file-lines" size={20} color={colors.text_color_secondary} /></View>
                  <Text weight="medium" style={{ flex: 1 }}>{provider.name}</Text>
                  <Icon name={collapsedProviders[provider.id] ? 'chevron-right' : 'chevron-down'} size={11} color={colors.text_color_secondary} />
                </Pressable>
                <Button text={t('settings.import.importAction')} accessibilityLabel={t('settings.import.importFrom', { source: provider.name })} variant="secondary" size="small"
                  isLoading={isProcessing && activeProvider === provider.id}
                  isDisabled={isProcessing || localBusy} onPress={() => handleImport(provider.id)} />
              </View>
              {!collapsedProviders[provider.id] && <View style={styles.providerHelp}>
                {[1, 2, 3].map(step => <Text key={step} size="s" color="secondary" style={styles.instructionStep}>
                  {t(`settings.import.${provider.prefix}Step${step}`)}
                </Text>)}
              </View>}
            </View>
          ))}
        </View>
        <Text size="s" color="secondary">{t('settings.import.exportHint')}</Text>
        {isProcessing && <Text size="s" color="secondary">{getStatusText()}</Text>}

        {showProgressBar && (
          <View style={styles.progressContainer}>
            <View style={styles.progressBarBackground}>
              <View
                style={[
                  styles.progressBarFill,
                  showIndeterminateBar
                    ? { width: '30%', opacity: 0.6 }
                    : { width: `${progressBarPercent}%` },
                ]}
              />
            </View>
            {!showIndeterminateBar && (
              <Text size="s" color="secondary" style={styles.progressText}>
                {progressBarPercent}%
              </Text>
            )}
          </View>
        )}

        {isProcessing && (
          <Button
            text={t('settings.import.cancel')}
            variant="muted"
            size="small"
            onPress={handleCancel}
            style={{ marginTop: 8, alignSelf: 'flex-start' }}
          />
        )}
      </View>

      {importState === IMPORT_STATES.DONE && result && (
        <SettingsSection title={t('settings.import.importResults')} isColumn>
          <View style={styles.resultContainer}>
            {detectedSource && (
              <View style={styles.resultRow}>
                <Icon name="circle-info" size={14} color={colors.text_color_secondary} />
                <Text size="s" color="secondary">
                  {t('settings.import.detectedSource').replace('%SOURCE%', detectedSource === 'claude' ? 'Claude' : detectedSource === 'grok' ? 'Grok' : detectedSource === 'gabai' ? 'Gab AI' : 'ChatGPT')}
                </Text>
              </View>
            )}

            {result.filesProcessed > 1 && (
              <View style={styles.resultRow}>
                <Icon name="layer-group" size={14} color={colors.text_color_secondary} />
                <Text size="s" color="secondary">
                  {t('settings.import.filesCombined').replace('%COUNT%', String(result.filesProcessed))}
                </Text>
              </View>
            )}

            <View style={styles.resultRow}>
              <Icon name="circle-check" size={14} color={colors.color_success} />
              <Text size="s">
                {t('settings.import.conversationsImported').replace('%COUNT%', String(result.imported))}
              </Text>
            </View>

            {result.skipped > 0 && (
              <View style={styles.resultRow}>
                <Icon name="circle-minus" size={14} color={colors.text_color_secondary} />
                <Text size="s" color="secondary">
                  {t('settings.import.duplicatesSkipped').replace('%COUNT%', String(result.skipped))}
                </Text>
              </View>
            )}

            {result.collectionsRestored > 0 && (
              <Text size="s">{t('settings.import.collectionsRestored').replace('%COUNT%', String(result.collectionsRestored))}</Text>
            )}
            {!result.hasCollectionMetadata && (
              <Text size="s" color="secondary">{t('settings.import.noCollectionMetadata')}</Text>
            )}
            {result.collectionWarnings.map((warning, index) => (
              <Text key={`folder-warning-${index}`} size="s" color="secondary">{warning}</Text>
            ))}

            {result.errors.length > 0 && (
              <View style={styles.errorsBlock}>
                <View style={styles.resultRow}>
                  <Icon name="circle-exclamation" size={14} color={colors.color_warning} />
                  <Text size="s" color="secondary">
                    {t('settings.import.errorsCount').replace('%COUNT%', String(result.errors.length))}
                  </Text>
                </View>
                <ScrollView
                  style={styles.errorsScroll}
                  nestedScrollEnabled
                  showsVerticalScrollIndicator
                >
                  {result.errors.slice(0, 80).map((line, idx) => (
                    <Text
                      key={`err-${idx}`}
                      size="s"
                      color="secondary"
                      style={styles.errorLine}
                    >
                      {line}
                    </Text>
                  ))}
                </ScrollView>
              </View>
            )}

            <Button
              text={t('settings.import.importMore')}
              variant="secondary"
              size="small"
              onPress={resetState}
              style={{ marginTop: 12 }}
            />
          </View>
        </SettingsSection>
      )}

      {importState === IMPORT_STATES.ERROR && errorMessage && (
        <SettingsSection title={t('settings.import.error')} isColumn>
          <View style={styles.errorContainer}>
            <Text size="s" color="danger">{errorMessage}</Text>
            <Button
              text={t('settings.import.tryAgain')}
              variant="secondary"
              size="small"
              onPress={resetState}
              style={{ marginTop: 12 }}
            />
          </View>
        </SettingsSection>
      )}

    </View>
  );
}

const createStyles = (colors) =>
  StyleSheet.create({
    progressContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      marginTop: 10,
      width: '100%',
    },
    progressBarBackground: {
      flex: 1,
      height: 6,
      backgroundColor: colors.border_color_secondary,
      borderRadius: 3,
      overflow: 'hidden',
    },
    progressBarFill: {
      height: '100%',
      backgroundColor: colors.text_color_primary,
      borderRadius: 3,
    },
    progressText: {
      minWidth: 32,
      textAlign: 'right',
    },
    resultContainer: {
      gap: 8,
      width: '100%',
    },
    resultRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
    },
    errorContainer: {
      width: '100%',
    },
    providerGroup: {
      borderWidth: 1, borderColor: colors.border_color_secondary, borderRadius: 12,
      overflow: 'hidden', width: '100%',
    },
    providerDivider: { borderTopWidth: 1, borderTopColor: colors.border_color_secondary },
    providerRow: { flexDirection: 'row', alignItems: 'center', gap: 16, padding: 16 },
    providerLabel: { flex: 1, flexDirection: 'row', alignItems: 'center', gap: 12, minWidth: 0 },
    providerIcon: { width: 32, alignItems: 'center' },
    providerHelp: { paddingHorizontal: 20, paddingBottom: 18, gap: 8 },
    instructionStep: {
      lineHeight: 18,
    },
    errorsBlock: {
      width: '100%',
      gap: 6,
    },
    errorsScroll: {
      maxHeight: 200,
      width: '100%',
      marginTop: 4,
    },
    errorLine: {
      marginBottom: 6,
      lineHeight: 18,
    },
  });

export default React.memo(ImportSettings);
